import { useState } from 'react'
import {
  Button,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from '@heroui/react'

import type {
  ReportJson,
  RunResult,
} from '../../../../../functions/tests/eval/phase1/types'

import { TurnsAsBubbles } from './bubbles'
import { checkConsistency } from './consistency'
import type { LoadedFixture } from './dataLoaders'
import { fmtCost, fmtMs, fmtPct } from './formatters'

interface FixtureCardProps {
  fixture: LoadedFixture
  /** When set, render the per-model run-result panel beneath the fixture body. */
  report: ReportJson | null
  /** Initially-expanded state for the run-result panel. */
  defaultExpanded?: boolean
}

function statusEmoji(r: RunResult): string {
  if (r.error) return '❌'
  if (!r.score) return '—'
  return r.score.isClean ? '✅' : '⚠️'
}

/** Failures sort to the top so they catch the eye. */
function sortByFailureFirst(results: RunResult[]): RunResult[] {
  return [...results].sort((a, b) => {
    const aFail = a.error !== null || (a.score !== null && !a.score.isClean)
    const bFail = b.error !== null || (b.score !== null && !b.score.isClean)
    if (aFail !== bFail) return aFail ? -1 : 1
    return a.modelKey.localeCompare(b.modelKey)
  })
}

export function FixtureCard({
  fixture,
  report,
  defaultExpanded = false,
}: FixtureCardProps) {
  const { meta, inputText, sourceUrl } = fixture
  const consistency = checkConsistency(meta)
  const fixturePath = `functions/tests/eval/phase1/fixtures/${fixture.slug}/meta.json`

  const fixtureResults =
    report?.results.filter((r) => r.fixtureId === meta.id) ?? []
  const cleanCount = fixtureResults.filter((r) => r.score?.isClean).length
  const totalCalls = fixtureResults.length
  const allClean = totalCalls > 0 && cleanCount === totalCalls

  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <Card id={`fixture-${fixture.slug}`} className="p-5 space-y-4 scroll-mt-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-mono font-semibold text-slate-900 dark:text-slate-100">
              {meta.id}
            </h2>
            {consistency.ok ? (
              <Tooltip content="Self-consistency checks passed">
                <Chip size="sm" color="success" variant="flat">
                  ✓ consistent
                </Chip>
              </Tooltip>
            ) : (
              <Tooltip
                content={
                  <ul className="list-disc pl-4 max-w-md">
                    {consistency.issues.map((issue, i) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                }
              >
                <Chip size="sm" color="danger" variant="flat">
                  ⚠ {consistency.issues.length} issue
                  {consistency.issues.length === 1 ? '' : 's'}
                </Chip>
              </Tooltip>
            )}
            {totalCalls > 0 && (
              <Chip
                size="sm"
                color={allClean ? 'success' : 'warning'}
                variant="flat"
              >
                {cleanCount}/{totalCalls} clean
              </Chip>
            )}
          </div>
          <p className="text-sm italic text-slate-600 dark:text-slate-400">
            {meta.description}
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {meta.tags.map((tag) => (
            <Chip key={tag} size="sm" variant="bordered">
              {tag}
            </Chip>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <span>
          User context:{' '}
          {meta.input.userContext?.displayName ? (
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {meta.input.userContext.displayName}
            </span>
          ) : (
            <span className="italic">none</span>
          )}
          {meta.input.userContext?.email && (
            <>
              {' · '}
              <span className="font-mono">{meta.input.userContext.email}</span>
            </>
          )}
        </span>
        <span>·</span>
        <span>
          Expected: <strong>{meta.expected.turnCount}</strong> turns,{' '}
          <strong>{meta.expected.speakerCount}</strong> speaker
          {meta.expected.speakerCount === 1 ? '' : 's'}
        </span>
        <span>·</span>
        <Chip
          size="sm"
          variant="flat"
          color={meta.expected.userPresent ? 'primary' : 'default'}
        >
          {meta.expected.userPresent ? 'user in chat' : 'user not in chat'}
        </Chip>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Input
          </div>
          {sourceUrl && (
            <div className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
              <img
                src={sourceUrl}
                alt={`Source screenshot for ${meta.id}`}
                className="w-full h-auto"
              />
            </div>
          )}
          <pre className="text-xs leading-relaxed font-mono whitespace-pre-wrap break-words bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-slate-800 dark:text-slate-200">
            {inputText}
          </pre>
        </div>

        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Expected output
          </div>
          <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
            <TurnsAsBubbles expected={meta.expected} />
          </div>
        </div>
      </div>

      {report && totalCalls > 0 && (
        <div className="border-t border-slate-200 dark:border-slate-700 pt-3 space-y-3">
          <Button
            variant="light"
            size="sm"
            onPress={() => setExpanded((v) => !v)}
            className="justify-start px-2"
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
              {expanded ? '▾' : '▸'} Model results · {cleanCount}/{totalCalls}{' '}
              clean
            </span>
          </Button>
          {expanded && (
            <Table
              aria-label={`Per-model results for ${meta.id}`}
              removeWrapper
            >
              <TableHeader>
                <TableColumn>Model</TableColumn>
                <TableColumn>Mode</TableColumn>
                <TableColumn>Clean</TableColumn>
                <TableColumn>Turn</TableColumn>
                <TableColumn>Spkr#</TableColumn>
                <TableColumn>UserID</TableColumn>
                <TableColumn>Speaker</TableColumn>
                <TableColumn>MsgSim</TableColumn>
                <TableColumn>Conf</TableColumn>
                <TableColumn>Latency</TableColumn>
                <TableColumn>Cost</TableColumn>
              </TableHeader>
              <TableBody>
                {sortByFailureFirst(fixtureResults).map((r, i) => {
                  const model = report.models.find((m) => m.key === r.modelKey)
                  const display = model?.displayName ?? r.modelKey
                  const score = r.score
                  return (
                    <TableRow key={`${r.modelKey}-${r.mode}-${i}`}>
                      <TableCell>
                        {r.error ? (
                          <Tooltip content={r.error}>
                            <span className="text-danger-600">
                              {display} ❌
                            </span>
                          </Tooltip>
                        ) : (
                          display
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="sm"
                          variant="flat"
                          color={r.mode === 'image' ? 'secondary' : 'default'}
                        >
                          {r.mode === 'image' ? '🖼 image' : '📝 text'}
                        </Chip>
                      </TableCell>
                      <TableCell>{statusEmoji(r)}</TableCell>
                      <TableCell>
                        {score ? (score.turnCountMatch ? '✓' : '✗') : '—'}
                      </TableCell>
                      <TableCell>
                        {score ? (score.speakerCountMatch ? '✓' : '✗') : '—'}
                      </TableCell>
                      <TableCell>
                        {score ? (score.userPresenceMatch ? '✓' : '✗') : '—'}
                      </TableCell>
                      <TableCell>
                        {fmtPct(score?.speakerLabelAccuracy)}
                      </TableCell>
                      <TableCell>
                        {fmtPct(score?.messageSimilarityAvg)}
                      </TableCell>
                      <TableCell>{r.confidence?.toFixed(2) ?? '—'}</TableCell>
                      <TableCell>{fmtMs(r.latencyMs)}</TableCell>
                      <TableCell>{fmtCost(r.costUsd)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      <div className="text-[11px] font-mono text-slate-400 dark:text-slate-500 truncate">
        {fixturePath}
      </div>
    </Card>
  )
}
